import React, { memo, useEffect, useState } from 'react';

interface HistoryItem {
  _id: string;
  query: string;
  totalResults: number;
  noSiteResults: number;
  createdAt: string;
}

interface HistoryViewProps {
  onSelect: (query: string) => void;
  onBack: () => void;
  refreshTrigger: number;
}

export const HistoryView: React.FC<HistoryViewProps> = memo(({ onSelect, onBack, refreshTrigger }) => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/search/history');
        const data = await res.json();
        if (data.success) {
          setHistory(data.history || []);
        }
      } catch (err) {
        console.error('Failed to load history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [refreshTrigger]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filtered = searchFilter
    ? history.filter(h => h.query.toLowerCase().includes(searchFilter.toLowerCase()))
    : history;

  const totalBusinesses = history.reduce((sum, h) => sum + h.totalResults, 0);
  const totalNoSite = history.reduce((sum, h) => sum + h.noSiteResults, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Search History</h1>
              <p className="text-gray-500 mt-1">
                All your past searches — click any to load cached results (0 API credits)
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-4">
              <div className="text-center px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="text-lg font-bold text-blue-600">{history.length}</div>
                <div className="text-xs text-gray-500">Searches</div>
              </div>
              <div className="text-center px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="text-lg font-bold text-gray-900">{totalBusinesses.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Businesses</div>
              </div>
              <div className="text-center px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="text-lg font-bold text-emerald-600">{totalNoSite.toLocaleString()}</div>
                <div className="text-xs text-gray-500">No Website</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-6">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Filter history..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
            />
          </div>
        </div>

        {/* History List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {searchFilter ? 'No matching searches found' : 'No search history yet'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((item, index) => (
              <button
                key={item._id}
                onClick={() => onSelect(item.query)}
                className="w-full text-left group bg-white border border-gray-100 rounded-2xl px-5 py-4 hover:border-blue-200 hover:shadow-md hover:bg-blue-50/30 transition-all duration-200 flex items-center gap-4"
              >
                {/* Index */}
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center text-xs font-bold text-gray-400 group-hover:text-blue-600 transition-colors">
                  {index + 1}
                </span>

                {/* Query Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 capitalize truncate transition-colors">
                    {item.query}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatDate(item.createdAt)}
                  </p>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-700">{item.totalResults}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Total</div>
                  </div>
                  <div className="w-px h-8 bg-gray-200" />
                  <div className="text-right">
                    <div className="text-sm font-bold text-emerald-600">{item.noSiteResults}</div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">No Site</div>
                  </div>
                </div>

                {/* Arrow */}
                <svg className="w-5 h-5 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

HistoryView.displayName = 'HistoryView';
export default HistoryView;
